import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  ArrowPathIcon,
  FunnelIcon,
  EllipsisVerticalIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import {
  GlobeAltIcon as GlobeAltIconSolid,
} from '@heroicons/react/24/solid';
import { domainService } from '../../services/api';
import BreadcrumbNavigation from './BreadcrumbNavigation';
import { useAdminData } from '../../context/AdminDataContext';

// Design System Components
import Button from '../ui/Button';
import { SearchInput } from '../ui/Input';
import Table, { StatusBadge, ActionMenu, TablePagination } from '../ui/Table';
import Card, { CardHeader } from '../ui/Card';

// Domain-specific components
import AddDomainModal from './domain/AddDomainModal';
import ModernDomainVerification from './domain/ModernDomainVerification';
import DnsManagement from './domain/DnsManagement';
import logger from '../../utils/logger';

// Portal-based tooltip for SSL details (avoids overflow clipping)
const SSLTooltip = ({ domain, getSSLStatusText, onManageSSL }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const handleMouseEnter = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 8,
        left: Math.min(rect.left, window.innerWidth - 240)
      });
    }
    setShow(true);
  };

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShow(false)}
        className="cursor-help"
      >
        <ShieldCheckIcon className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
      </span>
      {show && createPortal(
        <div
          className="fixed z-[9999] bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-3 min-w-[220px]"
          style={{ top: pos.top, left: pos.left }}
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
        >
          <div className="space-y-2">
            <div className="text-xs font-medium text-gray-700 dark:text-gray-300">SSL Certificate Details</div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Status:</span>
                <span className={`font-medium ${
                  domain.sslStatus === 'valid' ? 'text-green-600' :
                  domain.sslStatus === 'expired' || domain.sslStatus === 'invalid' ? 'text-red-600' :
                  domain.sslStatus === 'expiring-soon' ? 'text-yellow-600' : 'text-gray-500'
                }`}>
                  {getSSLStatusText(domain.sslStatus)}
                </span>
              </div>
              {domain.sslIssuer && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Issuer:</span>
                  <span className="text-gray-900 dark:text-white">{domain.sslIssuer}</span>
                </div>
              )}
              {domain.sslExpiryDate && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Expires:</span>
                  <span className="text-gray-900 dark:text-white">{new Date(domain.sslExpiryDate).toLocaleDateString()}</span>
                </div>
              )}
              {domain.sslLastChecked && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Last checked:</span>
                  <span className="text-gray-400">{new Date(domain.sslLastChecked).toLocaleDateString()}</span>
                </div>
              )}
            </div>
            <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
              <button
                onClick={(e) => { e.stopPropagation(); onManageSSL(); }}
                className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#222222] hover:bg-gray-200 dark:hover:bg-[#2a2a2a] rounded-md transition-colors cursor-pointer"
              >
                <ShieldCheckIcon className="w-3.5 h-3.5" />
                Manage SSL
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const Domains = ({ showNotification }) => {
  const navigate = useNavigate();
  const { getDomains } = useAdminData();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [showAddDomainModal, setShowAddDomainModal] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    health: '',
    config: ''
  });
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [viewType, setViewType] = useState('domains');
  const domainsPerPage = 30;

  const fetchDomains = useCallback(async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      let response;
      
      // Use cache for initial load without search/filters
      if (page === 1 && !searchQuery && !filters.status && !filters.health && !filters.config) {
        try {

          response = await getDomains();
          if (!response?.domains) {
            throw new Error('No cached data');
          }
          // Format cached response to match expected structure
          response = { data: { domains: response.domains, pagination: response.pagination } };
        } catch (error) {

          const result = await domainService.getDomains({
            search: searchQuery,
            ...filters,
            page: page,
            limit: domainsPerPage
          });
          response = { data: result };
        }
      } else {
        // Direct API call for searches/filters/pagination
        const result = await domainService.getDomains({
          search: searchQuery,
          ...filters,
          page: page,
          limit: domainsPerPage
        });
        response = { data: result };
      }

      // Handle successful response with domains data
      if (response.data && response.data.domains) {
        const formattedDomains = await Promise.all(
          response.data.domains.map(async (domain) => {
            const formattedDomain = {
              ...domain,
              id: domain.id || domain._id,
              _id: domain._id || domain.id
            };
            
            // Set default SSL status - no default assignment, let it be null/undefined
            // SSL status will only show when actually checked
            
            return formattedDomain;
          })
        );
        
        if (append && page > 1) {
          setDomains(prevDomains => [...prevDomains, ...formattedDomains]);
        } else {
          setDomains(formattedDomains);
        }

        // Check if there are more pages
        const totalDomains = response.data.pagination?.total || formattedDomains.length;
        const totalPages = response.data.pagination?.pages || Math.ceil(totalDomains / domainsPerPage);
        const hasMorePages = page < totalPages;
        
        setHasMore(hasMorePages);
        setCurrentPage(page);
        setError('');
      } else {
        setDomains([]);
        setError('');
        setHasMore(false);
      }
    } catch (error) {
      logger.error('Error fetching domains:', error);
      if (page === 1) {
        const errorMessage = error.response?.data?.message || 'Failed to load domains';
        setError(errorMessage);
        showNotification && showNotification(errorMessage, 'error');
        setDomains([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchQuery, filters, showNotification, getDomains]);

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchDomains(1, false);
  }, [searchQuery, filters]);

  // Search handler function
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setSelectedDomains([]);
  }, []);

  // Handle adding domain
  const handleAddDomain = async (domainName) => {
    try {
      const result = await domainService.addDomain(domainName);

      if (result && result.success) {
        // Optimistically add new domain to the list immediately
        const newDomain = result.domain || {
          id: result.domainId || Date.now().toString(),
          _id: result.domainId || Date.now().toString(),
          domainName: domainName,
          name: domainName,
          status: 'pending',
          verified: false,
          createdAt: new Date().toISOString()
        };
        setDomains(prev => [{ ...newDomain, id: newDomain.id || newDomain._id, _id: newDomain._id || newDomain.id }, ...prev]);

        showNotification && showNotification('Domain added successfully', 'success');
        setShowAddDomainModal(false);

        // Refresh in background to get full data from server (force bypass cache)
        try {
          const freshResult = await domainService.getDomains({ page: 1, limit: domainsPerPage });
          if (freshResult?.domains) {
            const formattedDomains = freshResult.domains.map(d => ({
              ...d,
              id: d.id || d._id,
              _id: d._id || d.id
            }));
            setDomains(formattedDomains);
          }
        } catch (refreshErr) {
          // Silent - optimistic update is already in place
        }

        return true;
      }
    } catch (error) {
      logger.error('Error adding domain:', error);
      // 402 subscription guard returns the reason in `error`; route validation
      // uses `message`. Read both so the real reason is shown, not a generic one.
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to add domain';
      throw new Error(errorMessage);
    }
  };

  // Handle deleting domain
  const handleDeleteDomain = async (domainId) => {
    // Store the domain for potential rollback
    const domainToDelete = domains.find(d => d.id === domainId);

    try {
      // Optimistic update - remove domain immediately from UI
      setDomains(prev => prev.filter(d => d.id !== domainId));

      const result = await domainService.deleteDomain(domainId);

      if (result.success) {
        showNotification?.('Domain deleted successfully', 'success');
      }
    } catch (error) {
      logger.error('Error deleting domain:', error);
      // Rollback optimistic update on error
      if (domainToDelete) {
        setDomains(prev => [...prev, domainToDelete]);
      }
      const errorMessage = error.response?.data?.message || 'Failed to delete domain';
      showNotification?.(errorMessage, 'error');
    }
  };

  // Handle bulk actions
  const handleBulkDelete = async () => {
    if (selectedDomains.length === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedDomains.length} domain(s)?`)) {
      await Promise.all(selectedDomains.map(domainId => handleDeleteDomain(domainId)));
      setSelectedDomains([]);
    }
  };

  // Handle row click - open verification window
  const handleRowClick = (domain) => {
    setSelectedDomain(domain);
    setViewType('verification');
  };

  // SSL Check Function
  const checkSSLStatus = async (domain, showLoading = true) => {
    try {
      if (showLoading) {
        showNotification && showNotification('Checking SSL certificate...', 'info');
      }
      
      const result = await domainService.checkSSL(domain.id);

      if (result.success) {
        // Update the domain in the state
        setDomains(prevDomains =>
          prevDomains.map(d =>
            d.id === domain.id
              ? {
                  ...d,
                  sslStatus: result.sslInfo.status,
                  sslExpiryDate: result.sslInfo.expiryDate,
                  sslLastChecked: new Date().toISOString(),
                  sslIssuer: result.sslInfo.issuer
                }
              : d
          )
        );

        if (showLoading) {
          showNotification && showNotification('SSL certificate checked successfully', 'success');
        }

        return result.sslInfo;
      }
    } catch (error) {
      logger.error('SSL check failed:', error);
      if (showLoading) {
        showNotification && showNotification('SSL check failed', 'error');
      }
      
      // Update with failed status
      setDomains(prevDomains => 
        prevDomains.map(d => 
          d.id === domain.id 
            ? { ...d, sslStatus: 'check-failed', sslLastChecked: new Date().toISOString() }
            : d
        )
      );
    }
  };

  // Domain Health Check Function
  const checkDomainHealth = async (domain, showLoading = true) => {
    try {
      if (showLoading) {
        showNotification && showNotification('Checking domain health...', 'info');
      }
      
      const result = await domainService.healthCheck(domain.id);

      if (result.success) {
        // Update the domain in the state
        setDomains(prevDomains =>
          prevDomains.map(d =>
            d.id === domain.id
              ? {
                  ...d,
                  healthScore: result.health.overallScore,
                  healthStatus: result.health.overallScore >= 90 ? 'excellent' :
                               result.health.overallScore >= 70 ? 'good' :
                               result.health.overallScore >= 50 ? 'fair' : 'poor',
                  healthLastChecked: new Date().toISOString()
                }
              : d
          )
        );

        if (showLoading) {
          showNotification && showNotification('Domain health checked successfully', 'success');
        }

        return result.health;
      }
    } catch (error) {
      logger.error('Health check failed:', error);
      if (showLoading) {
        showNotification && showNotification('Health check failed', 'error');
      }
    }
  };

  // Handle domain action (verify, configure, etc.)
  const handleDomainAction = (domain, action) => {
    setSelectedDomain(domain);
    setViewType(action);
  };

  // Table sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedDomains.length === domains.length) {
      setSelectedDomains([]);
    } else {
      setSelectedDomains(domains.map(domain => domain.id));
    }
  };

  const handleDomainSelect = (domainId, checked) => {
    if (checked) {
      setSelectedDomains(prev => [...prev, domainId]);
    } else {
      setSelectedDomains(prev => prev.filter(id => id !== domainId));
    }
  };

  // Table columns
  const columns = [
    {
      id: 'select',
      header: (
        <input
          type="checkbox"
          className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
          onChange={toggleSelectAll}
          checked={domains.length > 0 && selectedDomains.length === domains.length}
        />
      ),
      cell: (domain) => (
        <input
          type="checkbox"
          className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
          checked={selectedDomains.includes(domain.id)}
          onChange={(e) => {
            e.stopPropagation();
            handleDomainSelect(domain.id, e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      width: '40px'
    },
    {
      id: 'domain',
      header: 'Domain',
      cell: (domain) => (
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <GlobeAltIcon className="w-4 h-4 text-orange-600" />
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">{domain.domainName}</div>
            <div className="text-xs text-gray-500">Added {new Date(domain.createdAt).toLocaleDateString()}</div>
          </div>
        </div>
      ),
      sortable: true
    },
    {
      id: 'status',
      header: 'Status',
      cell: (domain) => (
        <StatusBadge 
          status={domain.isVerified ? 'verified' : 'pending'} 
          color={domain.isVerified ? 'success' : 'warning'}
        >
          {domain.isVerified ? 'Verified' : 'Pending'}
        </StatusBadge>
      ),
      sortable: true
    },
    {
      id: 'health',
      header: 'Health',
      cell: (domain) => {
        const healthScore = domain.healthScore || (domain.isVerified ? 85 : 0);
        const getHealthColor = (score) => {
          if (score >= 90) return 'bg-green-400';
          if (score >= 70) return 'bg-yellow-400';
          if (score >= 50) return 'bg-orange-400';
          return 'bg-red-400';
        };

        return (
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${getHealthColor(healthScore)}`}></div>
            <span className="text-sm text-gray-900 dark:text-white">{healthScore}%</span>
            {domain.isVerified && (
              <Button
                variant="ghost"
                size="xs"
                onClick={(e) => {
                  e.stopPropagation();
                  // Check domain health
                  checkDomainHealth(domain);
                }}
                icon={<ArrowPathIcon />}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1"
                title="Check Health"
              />
            )}
          </div>
        );
      },
      sortable: true
    },
    {
      id: 'ssl',
      header: 'SSL',
      cell: (domain) => {
        const getSSLStatusColor = (status) => {
          switch (status) {
            case 'valid': return 'success';
            case 'expired': return 'error';
            case 'expiring-soon': return 'warning';
            case 'invalid': return 'error';
            default: return 'gray';
          }
        };

        const getSSLStatusText = (status) => {
          switch (status) {
            case 'valid': return 'Valid';
            case 'expired': return 'Expired';
            case 'expiring-soon': return 'Expiring';
            case 'invalid': return 'Invalid';
            case 'no-certificate': return 'No SSL';
            case 'timeout': return 'Timeout';
            default: return domain.sslStatus ? 'Check Failed' : 'Not Checked';
          }
        };

        // Only show badge if SSL status exists or if domain is verified
        const showSSLStatus = domain.sslStatus || domain.isVerified;

        return (
          <div className="flex items-center space-x-2 group">
            {showSSLStatus && (
              <StatusBadge
                status={getSSLStatusText(domain.sslStatus)}
                color={getSSLStatusColor(domain.sslStatus)}
              />
            )}

            {!showSSLStatus && domain.isVerified && (
              <span className="text-sm text-gray-500">Not Checked</span>
            )}

            {domain.isVerified && (
              <Button
                variant="ghost"
                size="xs"
                onClick={(e) => {
                  e.stopPropagation();
                  checkSSLStatus(domain);
                }}
                icon={<ArrowPathIcon />}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1"
                title="Check SSL"
              />
            )}

            {showSSLStatus && (
              <SSLTooltip
                domain={domain}
                getSSLStatusText={getSSLStatusText}
                onManageSSL={() => handleDomainAction(domain, 'ssl')}
              />
            )}
          </div>
        );
      },
      sortable: true
    },
    {
      id: 'users',
      header: 'Users',
      cell: (domain) => (
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-900 dark:text-white">
            {domain.userCount || 0}
          </span>
          {domain.userCount > 0 && (
            <Button
              variant="ghost"
              size="xs"
              onClick={(e) => {
                e.stopPropagation();
                // Navigate to users section with domain filter in query params
                navigate(`/users?domain=${encodeURIComponent(domain.domainName)}`);
              }}
              className="text-orange-600 hover:text-orange-700 text-xs px-2 py-1"
            >
              View
            </Button>
          )}
        </div>
      ),
      sortable: true
    },
    {
      id: 'actions',
      header: '',
      cell: (domain) => (
        <div className="flex items-center justify-center">
          <ActionMenu
            items={[
              {
                label: 'Verify Domain',
                icon: <CheckCircleIcon className="w-4 h-4" />,
                onClick: () => handleDomainAction(domain, 'verification'),
                disabled: domain.isVerified
              },
              {
                label: 'DNS Management',
                icon: <CogIcon className="w-4 h-4" />,
                onClick: () => handleDomainAction(domain, 'dns'),
                disabled: false // Enable for now - shows DNS records
              },
              {
                label: 'Check SSL',
                icon: <ShieldCheckIcon className="w-4 h-4" />,
                onClick: () => checkSSLStatus(domain),
                disabled: !domain.isVerified
              },
              {
                label: 'Check Health',
                icon: <ArrowPathIcon className="w-4 h-4" />,
                onClick: () => checkDomainHealth(domain),
                disabled: !domain.isVerified
              },
              { type: 'divider' },
              {
                label: 'Delete',
                icon: <TrashIcon className="w-4 h-4" />,
                onClick: () => handleDeleteDomain(domain.id),
                className: 'text-red-600 hover:text-red-700',
                confirm: true,
                confirmMessage: `Are you sure you want to delete ${domain.domainName}?`
              }
            ]}
          />
        </div>
      ),
      width: '48px'
    }
  ];

  // Render different views based on viewType
  if (viewType !== 'domains') {
    switch (viewType) {
      case 'verification':
        return (
          <ModernDomainVerification 
            domain={selectedDomain} 
            onBack={() => setViewType('domains')} 
            onVerified={() => {
              fetchDomains();
              setViewType('domains');
            }}
            showNotification={showNotification}
          />
        );
      case 'dns':
        return (
          <DnsManagement 
            domain={selectedDomain} 
            onBack={() => setViewType('domains')} 
          />
        );
      default:
        return null;
    }
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-[#111111] overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800">
        <div className="px-6 py-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <GlobeAltIconSolid className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              <h1 className="text-sm font-semibold text-gray-900 dark:text-white">Domain Management</h1>
              <span className="text-xs text-gray-400 dark:text-gray-500">{domains.length} {domains.length === 1 ? 'domain' : 'domains'}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAddDomainModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-orange-600 hover:bg-orange-700 text-white rounded-md transition-colors"
              >
                <PlusIcon className="w-3.5 h-3.5" />
                Add Domain
              </button>
              <button
                onClick={async () => {
                  setLoading(true);
                  try {
                    const result = await domainService.getDomains({ page: 1, limit: domainsPerPage });
                    if (result?.domains) {
                      const formattedDomains = result.domains.map(d => ({
                        ...d,
                        id: d.id || d._id,
                        _id: d._id || d.id
                      }));
                      setDomains(formattedDomains);
                      setCurrentPage(1);
                      const totalPages = result.pagination?.pages || 1;
                      setHasMore(1 < totalPages);
                    }
                  } catch (err) {
                    showNotification && showNotification('Failed to refresh', 'error');
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="p-1.5 rounded-md bg-gray-100 dark:bg-[#222222] hover:bg-gray-200 dark:hover:bg-[#2a2a2a] transition-colors disabled:opacity-50"
              >
                <ArrowPathIcon className={`w-3.5 h-3.5 text-gray-600 dark:text-gray-300 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
          
          {/* Compact Search and Filters */}
          <div className="mb-3">
            <div className="flex gap-2">
              <div className="flex-1">
                <SearchInput
                  placeholder="Search domains..."
                  onSearch={handleSearch}
                  size="sm"
                />
              </div>
              <Button
                variant="secondary"
                size="sm"
                icon={<FunnelIcon />}
                className="relative flex-shrink-0"
              >
                {(filters.status || filters.health || filters.config) && (
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full"></div>
                )}
              </Button>
            </div>
          </div>

          {/* Selection Actions Bar */}
          {selectedDomains.length > 0 && (
            <div className="mb-3">
              <Card padding="sm" className="bg-orange-50 border-orange-200">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-orange-800">
                    {selectedDomains.length} selected
                  </span>
                  <div className="w-px h-4 bg-orange-300"></div>
                  <div className="flex gap-1">
                    <Button
                      variant="danger"
                      size="xs"
                      onClick={handleBulkDelete}
                      icon={<TrashIcon />}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 min-h-0">
        <div className="h-full overflow-auto p-6">
          {error && (
            <Card className="mb-6 border-red-200 bg-red-50">
              <div className="flex items-center gap-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800">Error loading domains</p>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => setError('')}
                  className="ml-auto text-red-600 hover:text-red-700"
                >
                  ×
                </Button>
              </div>
            </Card>
          )}

          {/* Domains Table */}
          <div>
            <Table
              columns={columns}
              data={domains}
              loading={loading}
              onRowClick={handleRowClick}
              onSort={handleSort}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              emptyState={{
                icon: <GlobeAltIconSolid className="w-12 h-12 text-gray-400" />,
                title: domains.length === 0 ? "No domains found" : "No domains match your search",
                description: domains.length === 0 
                  ? "Add your first domain to get started with email management"
                  : "Try adjusting your search or filter criteria",
                action: domains.length === 0 ? (
                  <Button
                    variant="primary"
                    onClick={() => setShowAddDomainModal(true)}
                    icon={<PlusIcon />}
                  >
                    Add Domain
                  </Button>
                ) : null
              }}
            />
          </div>

          {/* Load More */}
          {hasMore && domains.length > 0 && (
            <div className="flex justify-center pt-6">
              <Button
                variant="ghost"
                onClick={() => fetchDomains(currentPage + 1, true)}
                loading={loadingMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </Button>
            </div>
          )}

          {!hasMore && domains.length > 0 && (
            <div className="text-center pt-6">
              <span className="text-sm text-gray-500">No more domains to load</span>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <AddDomainModal
        open={showAddDomainModal}
        onClose={() => setShowAddDomainModal(false)}
        onAdd={handleAddDomain}
        loading={loading}
      />
    </div>
  );
};

export default Domains;